import { Head, router, useHttp, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { notificationsSeenMarkerPath, rootPath } from "@travily/routes";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useEffect, useRef } from "react";
import type { NotificationAction } from "@/components/NotificationBell/NotificationItem";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";
import { NotificationsShowView } from "./show.view";

export default function NotificationsShowPage() {
  // Read the live list straight from the shared props. PageFrame (rendered
  // below) owns the single NotificationChannel subscription; its broadcasts
  // refresh these props, so this page stays live without opening a second
  // subscription of its own (which previously double-processed every broadcast).
  const { notifications, unseen_notifications_count } = usePage<SharedProps>().props;
  const onBack = useNavigateBack(rootPath());
  const seenHttp = useHttp({});
  const hasMarkedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: guarded by hasMarkedRef; fires once when unseen first becomes positive
  useEffect(() => {
    if (hasMarkedRef.current || (unseen_notifications_count ?? 0) === 0) return;
    hasMarkedRef.current = true;
    seenHttp.post(notificationsSeenMarkerPath(), {
      onSuccess: () => router.reload({ only: ["unseen_notifications_count", "notifications"] }),
    });
  }, [unseen_notifications_count]);

  const handleNotificationAction = (action: NotificationAction) => {
    router[action.method](action.href);
  };

  return (
    <PageFrame
      contentMaxWidth="small"
      hideBottomBar
      hideTopBarNav
      mobileTopBar={<TopBarMobile bordered onBack={onBack} title={t("web.notifications.show.title")} />}
    >
      <Head title={t("web.notifications.show.meta_title")} />
      <NotificationsShowView notifications={notifications ?? []} onAction={handleNotificationAction} />
    </PageFrame>
  );
}
